import React, { useState } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { t } from '../../../../../locales/translate';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { Event } from '../../../../../model/event';
import { ResaleSetting, ResaleSettingForm } from './ResaleSettingForm';
import { Modal } from '../../../../../components/Modal/Modal';
import { graphql } from '../../../../../generated';
import { camelCase } from 'lodash';
import { useQueryHandler } from '../../../../../model/graphql';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { Grid } from '../../../../../components/Grid/Grid';
import { Tooltip } from '../../../../../components/Tooltip/Tooltip';
import { Icon } from '../../../../../components/Icon/Icon';
import { Stack } from '../../../../../components/Stack/Stack';

interface Props {
  event: Event;
}

const EVENT_RESALE_SETTING = graphql(`
  query ResaleSetting($eventId: ID!) {
    event(id: $eventId) {
      id
      resaleSetting {
        preference
        visibility
      }
    }
  }
`);

export function ResaleSettingInfo({ event }: Props): JSX.Element {
  const [displayModal, setDisplayModal] = useState(false);
  const { data, refetch } = useQueryHandler(EVENT_RESALE_SETTING, { variables: { eventId: event.id } });
  const preference = data?.event?.resaleSetting?.preference;
  const visibilities = data?.event?.resaleSetting?.visibility;

  return (
    <Grid>
      <section className="resale-info__section">
        <Card
          title={
            <Stack line size="xs">
              {t.model.event.resale.info}
              <Tooltip
                position="top"
                darkMode
                rounded
                reference={<Icon name="question--sm" />}
                style={{ width: 350, fontSize: 'small' }}
              >
                <p>{t.model.event.resale.tooltip}</p>
              </Tooltip>
            </Stack>
          }
          actions={
            <PermissionButton
              name="edit"
              title={t.model.event.resale.editSettings}
              model={event}
              permission="update_resale_settings"
              onClick={() => setDisplayModal(true)}
            />
          }
        >
          <KeyValueTable
            items={[
              {
                key: t.model.event.resale.resalePreference,
                value: t.model.event.resale.preferences[camelCase(preference)],
              },
              preference !== 'OFF' && {
                key: t.model.event.resale.visibilitySettings,
                value: visibilities?.map((v) => t.model.event.resale[camelCase(v)]).join(', '),
              },
            ]}
          />
        </Card>
        <Modal open={displayModal} title={t.model.event.resale.editSettings}>
          <ResaleSettingForm
            productId={event.id}
            onClose={() => {
              refetch();
              setDisplayModal(false);
            }}
            resaleSetting={data?.event?.resaleSetting as ResaleSetting}
          />
        </Modal>
      </section>
    </Grid>
  );
}
